import * as THREE from 'three'

/** Creates a rounded rectangle shape centered at origin */
export const createRoundedRectShape = (w: number, h: number, r: number): THREE.Shape => {
  const shape = new THREE.Shape()
  const hw = w / 2
  const hh = h / 2
  const cr = Math.max(0, Math.min(r, hw, hh))

  shape.moveTo(-hw + cr, -hh)
  shape.lineTo(hw - cr, -hh)
  if (cr > 0) shape.quadraticCurveTo(hw, -hh, hw, -hh + cr)
  else shape.lineTo(hw, -hh)
  shape.lineTo(hw, hh - cr)
  if (cr > 0) shape.quadraticCurveTo(hw, hh, hw - cr, hh)
  else shape.lineTo(hw, hh)
  shape.lineTo(-hw + cr, hh)
  if (cr > 0) shape.quadraticCurveTo(-hw, hh, -hw, hh - cr)
  else shape.lineTo(-hw, hh)
  shape.lineTo(-hw, -hh + cr)
  if (cr > 0) shape.quadraticCurveTo(-hw, -hh, -hw + cr, -hh)
  else shape.lineTo(-hw, -hh)

  return shape
}

/**
 * Recomputes UV coordinates using box projection from vertex positions.
 * Maps each face's UVs based on the dominant axis of its normal:
 *   - Front/back faces (Z-dominant): u = x, v = y
 *   - Left/right faces (X-dominant): u = z, v = y
 *   - Top/bottom faces (Y-dominant): u = x, v = z
 * UVs are normalized to 0..1 based on geometry bounding box.
 */
export const computeBoxProjectionUVs = (geometry: THREE.BufferGeometry, scale: number = 20): void => {
  const posAttr = geometry.getAttribute('position')
  const normalAttr = geometry.getAttribute('normal')

  if (!posAttr || !normalAttr) return

  // The body CSG ops now run without a 'uv' attribute (see deviceGeometryWorker),
  // so allocate one when missing — this function is the sole producer of the
  // body's box-projected UVs (brushed-metal normal/roughness maps).
  let uvAttr = geometry.getAttribute('uv')
  if (!uvAttr) {
    uvAttr = new THREE.BufferAttribute(new Float32Array(posAttr.count * 2), 2)
    geometry.setAttribute('uv', uvAttr)
  }

  for (let i = 0; i < posAttr.count; i++) {
    const nx = Math.abs(normalAttr.getX(i))
    const ny = Math.abs(normalAttr.getY(i))
    const nz = Math.abs(normalAttr.getZ(i))

    const px = posAttr.getX(i)
    const py = posAttr.getY(i)
    const pz = posAttr.getZ(i)

    let u: number
    let v: number

    if (nz >= nx && nz >= ny) {
      // Front/back face — use XY
      u = px / scale
      v = py / scale
    } else if (nx >= ny) {
      // Left/right face — use ZY
      u = pz / scale
      v = py / scale
    } else {
      // Top/bottom face — use XZ
      u = px / scale
      v = pz / scale
    }

    uvAttr.setXY(i, u, v)
  }

  uvAttr.needsUpdate = true
}
